import React from 'react';
import { ScoreGauge } from './ScoreGauge';
import { EnhancedCreditBureauData, formatCurrency, formatDate } from '../utils/creditBureauUtils';

interface CreditBureauResultsProps {
  data: EnhancedCreditBureauData;
}

interface InfoRowProps {
  label: string;
  value: any;
  valueClassName?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, valueClassName = '' }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm font-medium text-gray-600">{label}</span>
    <span className={`text-sm text-gray-900 ${valueClassName}`}>
      {value || 'N/A'}
    </span>
  </div>
);

interface AnalysisCardProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

const AnalysisCard: React.FC<AnalysisCardProps> = ({ title, children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
    <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">{title}</h3>
    {children}
  </div>
);

export const CreditBureauResults: React.FC<CreditBureauResultsProps> = ({ data }) => {
  return (
    <div className="space-y-6">
      {/* Header with Report Info */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Credit Bureau Report</h2>
            <p className="text-blue-100">Comprehensive Credit Analysis</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm opacity-90">Report ID</div>
              <div className="font-mono">{data.reportId}</div>
              <div className="text-xs opacity-75 mt-1">
                {new Date(data.lastUpdated).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Score Section */}
      <AnalysisCard title="Credit Score Overview">
        <div className="flex flex-col lg:flex-row items-center space-y-6 lg:space-y-0 lg:space-x-8">
          <div className="flex-shrink-0">
            <ScoreGauge 
              score={data.score} 
              maxScore={data.maxScore} 
              grade={data.grade}
              finalScore={data.finalScore}
              finalGrade={data.finalGrade}
            />
          </div>
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{data.score}</div>
                <div className="text-sm text-gray-600">Current Score</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{data.finalScore}</div>
                <div className="text-sm text-gray-600">Adjusted Score</div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Eligibility Status</h4>
              <p className="text-sm text-blue-800">{data.eligibility}</p>
            </div>
          </div>
        </div>
      </AnalysisCard>

      {/* Personal Information */}
      <AnalysisCard title="Personal Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <InfoRow label="Full Name" value={data.personalInfo?.name} />
            <InfoRow label="PAN Number" value={data.personalInfo?.pan} />
            <InfoRow label="Gender" value={data.personalInfo?.gender} />
          </div>
          <div className="space-y-2">
            <InfoRow label="Mobile Number" value={data.personalInfo?.mobile} />
            <InfoRow label="Date of Birth" value={data.personalInfo?.dob} />
            <InfoRow 
              label="Credit Score" 
              value={data.personalInfo?.credit_score} 
              valueClassName="font-semibold"
            />
          </div>
        </div>
      </AnalysisCard>

      {/* Risk Assessment */}
      {data.riskFlags && data.riskFlags.length > 0 && (
        <AnalysisCard title="Risk Assessment" className="border-l-4 border-red-500">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Risk Indicators
            </h4>
            <ul className="space-y-2">
              {data.riskFlags.map((flag, index) => (
                <li key={index} className="text-sm text-red-800 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{flag}</span>
                </li>
              ))}
            </ul>
          </div>
        </AnalysisCard>
      )}

      {/* Advisory Tips */}
      {data.advisoryTips && data.advisoryTips.length > 0 && (
        <AnalysisCard title="Recommendations" className="border-l-4 border-blue-500">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Credit Improvement Tips
            </h4>
            <ul className="space-y-2">
              {data.advisoryTips.map((tip, index) => (
                <li key={index} className="text-sm text-blue-800 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </AnalysisCard>
      )}

      {/* DPD Status Analysis */}
      {data.dpdStatusAnalysis && data.dpdStatusAnalysis.length > 0 && (
        <AnalysisCard title="DPD Status Analysis" className="border-l-4 border-yellow-500">
          <div className="mb-4">
            <span className="text-sm text-gray-600">
              Total Accounts Analyzed: {data.dpdStatusAnalysis.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Payment
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    EMI Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DPD Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SMA Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.dpdStatusAnalysis.map((dpd, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dpd["Loan Type"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dpd["Last Payment Date"] ? formatDate(dpd["Last Payment Date"]) : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {dpd["EMI Amount"] ? formatCurrency(dpd["EMI Amount"]) : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        String(dpd["DPD Status"]) === '0' 
                          ? 'bg-green-100 text-green-800'
                          : parseInt(String(dpd["DPD Status"])) > 90
                          ? 'bg-red-100 text-red-800'
                          : parseInt(String(dpd["DPD Status"])) > 30
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {dpd["DPD Status"] || '0'} days
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        String(dpd["SMA Status"]).toLowerCase() === 'standard' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {dpd["SMA Status"] || 'Standard'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Credit Facilities Overview */}
      {data.creditFacilitiesOverview && data.creditFacilitiesOverview.length > 0 && (
        <AnalysisCard title="Credit Facilities Overview">
          <div className="mb-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Total Active Accounts: {data.creditFacilitiesOverview.length}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              Monthly EMI: {formatCurrency(data.totalEmiAmount)}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sanctioned Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Balance
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    EMI
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.creditFacilitiesOverview.map((facility, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {facility["Loan Type"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {facility["Bank"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        String(facility["Status"]).toLowerCase().includes('active') 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {facility["Status"] || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(facility["Loan Sanctioned (INR)"])}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(facility["Current Balance (INR)"])}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(facility["EMI Amount (INR)"])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Estimated EMI Obligations */}
      {(data.estimatedEmiObligations && data.estimatedEmiObligations.length > 0) || 
       (data.creditFacilitiesOverview && data.creditFacilitiesOverview.length > 0) ? (
        <AnalysisCard title="Estimated EMI Obligations" className="border-l-4 border-indigo-500">
          <div className="mb-4">
            <span className="text-sm text-gray-600">
              Total Monthly EMI: {formatCurrency(data.totalEmiAmount)}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Outstanding
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estimated EMI
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(data.estimatedEmiObligations && data.estimatedEmiObligations.length > 0 
                  ? data.estimatedEmiObligations 
                  : data.creditFacilitiesOverview?.map(facility => ({
                      "Loan Type": facility["Loan Type"] || 'Unknown',
                      "Bank": facility["Bank"] || 'Unknown',
                      "Outstanding (INR)": parseFloat(String(facility["Current Balance (INR)"] || 0)) || 0,
                      "Estimated EMI (INR)": parseFloat(String(facility["EMI Amount (INR)"] || 0)) || 0
                    })) || []
                ).map((emi, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {emi["Loan Type"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {emi["Bank"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(emi["Outstanding (INR)"])}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-indigo-600">
                      {formatCurrency(emi["Estimated EMI (INR)"])}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900">
                    {formatCurrency((() => {
                      if (data.estimatedEmiObligations && data.estimatedEmiObligations.length > 0) {
                        return data.estimatedEmiObligations.reduce((sum: number, emi: any) => sum + (emi["Outstanding (INR)"] || 0), 0);
                      } else {
                        const fallbackObligations = data.creditFacilitiesOverview?.map(facility => ({
                          "Outstanding (INR)": parseFloat(String(facility["Current Balance (INR)"] || 0)) || 0
                        })) || [];
                        return fallbackObligations.reduce((sum: number, emi: any) => sum + (emi["Outstanding (INR)"] || 0), 0);
                      }
                    })())}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-indigo-600">
                    {formatCurrency(data.totalEmiAmount || 
                      (data.creditFacilitiesOverview?.reduce((sum: number, facility: any) => 
                        sum + (parseFloat(String(facility["EMI Amount (INR)"] || 0)) || 0), 0) || 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      ) : null}

      {/* Loan Appraisal Scorecard */}
      {data.loanAppraisalScorecard && data.loanAppraisalScorecard.length > 0 && (
        <AnalysisCard title="Credit Assessment Scorecard">
          <div className="space-y-4">
            {data.loanAppraisalScorecard.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{item.Metric || 'Unknown Metric'}</h5>
                    <p className="text-sm text-gray-600">{item.Value || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                      {item.Weight || '5%'}
                    </span>
                  </div>
                </div>
                {item.Remarks && (
                  <p className="text-sm text-gray-500 italic">{item.Remarks}</p>
                )}
              </div>
            ))}
          </div>
        </AnalysisCard>
      )}

      {/* Closed Loan Accounts */}
      {data.closedLoanAccounts && data.closedLoanAccounts.length > 0 && (
        <AnalysisCard title="Closed Loan Accounts" className="border-l-4 border-gray-500">
          <div className="mb-4">
            <span className="text-sm text-gray-600">
              Total Closed Accounts: {data.closedLoanAccounts.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sanctioned Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Closure Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.closedLoanAccounts.map((account, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account["Loan Type"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account["Bank"] || 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {account["Loan Sanctioned (INR)"] ? formatCurrency(account["Loan Sanctioned (INR)"]) : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account["Closure Date"] ? formatDate(account["Closure Date"]) : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {account["Status"] || account["Remarks"] || 'Closed'}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td colSpan={2} className="px-4 py-3 text-sm font-bold text-gray-900">
                    Total Sanctioned (Closed)
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900">
                    {formatCurrency(data.closedLoanAccounts.reduce((sum, account) => 
                      sum + (parseFloat(String(account["Loan Sanctioned (INR)"] || 0)) || 0), 0))}
                  </td>
                  <td colSpan={2} className="px-4 py-3 text-sm text-gray-500">
                    Historical loan accounts
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </AnalysisCard>
      )}

      {/* Enquiry Details */}
      {data.enquiryDetails && data.enquiryDetails.length > 0 && (
        <AnalysisCard title="Recent Credit Enquiries (Past 6 Months)">
          <div className="mb-4">
            <span className="text-sm text-gray-600">
              Total Enquiries: {data.enquiryDetails.length}
            </span>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {data.enquiryDetails.map((enquiry, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {enquiry.type || enquiry["Enquiry Type"] || 'Credit Enquiry'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {enquiry.date || enquiry["Date"] || 'Date not available'}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {enquiry.institution || enquiry["Institution"] || 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </AnalysisCard>
      )}

      {/* Observations and Final Notes */}
      <AnalysisCard title="Summary & Observations">
        <div className="space-y-3">
          {data.observationsAndFinalNotes.map((note, index) => (
            <div key={index} className="flex items-start space-x-2">
              <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <p className="text-sm text-gray-700">{note}</p>
            </div>
          ))}
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-2">Next Steps</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Monitor your credit report quarterly for changes and improvements</li>
              <li>• Focus on the top 3 advisory recommendations for maximum impact</li>
              <li>• Consider setting up automatic payments to improve payment history</li>
              <li>• Review and optimize your credit utilization across all accounts</li>
            </ul>
          </div>
        </div>
      </AnalysisCard>
    </div>
  );
};
